import { useEffect, useRef } from 'react';
import { animations } from '@/features/animations';
import { useScene } from '@/hooks/useScene';
import { Connector } from '@/types';
import { DEFAULT_COLOR } from 'src/config';

// Use the proper connector type from useScene which includes the path
type ConnectorWithPath = ReturnType<typeof useScene>['connectors'][0];

export function useConnectorFlow(connector: ConnectorWithPath) {
    const animationIdRef = useRef<string | null>(null);

    useEffect(() => {
        // Stop existing animation if any
        if (animationIdRef.current) {
            animations.stop(animationIdRef.current);
            animationIdRef.current = null;
        }

        // Start flow animation if enabled
        if (connector.flowEnabled && connector.path?.tiles) {
            const direction = determineFlowDirection(connector);

            animationIdRef.current = animations.dataFlow(
                connector,
                connector.path.tiles,
                {
                    speed: connector.flowSpeed || 1,
                    particleCount: connector.flowParticleCount || 5,
                    particleSize: connector.flowParticleSize || 8,
                    particleColor: connector.color || DEFAULT_COLOR.value,
                    direction,
                    loop: true,
                    duration: 3000
                }
            );
        }

        // Cleanup on unmount
        return () => {
            if (animationIdRef.current) {
                animations.stop(animationIdRef.current);
            }
        };
    }, [
        connector.flowEnabled,
        connector.flowSpeed,
        connector.flowParticleCount,
        connector.flowParticleSize,
        connector.arrowType,
        connector.color,
        connector.path
    ]);

    return animationIdRef.current;
}

/**
 * Determine flow direction based on arrow type
 */
function determineFlowDirection(
    connector: Connector
): 'forward' | 'backward' | 'bidirectional' {
    // If explicit direction is set, use it
    if (connector.flowDirection) {
        return connector.flowDirection;
    }

    // Otherwise, infer from arrow type
    switch (connector.arrowType) {
        case 'DOUBLE':
            return 'bidirectional';
        case 'SINGLE':
            return 'forward';
        case 'NONE':
        default:
            return 'forward'; // Default to forward
    }
}
